import { type Context, Controller } from '@hotwired/stimulus';
import { EVENT_TASK_PICK_RESOURCE_RESULTS } from 'src/events';
import { toggleHidden } from 'src/utils/dom-toggle';

export default class TasksResourcePickerController extends Controller {
  public static values = {
    type: String,
    current: {
      type: String,
      default: '',
    },
    currentDsName: String,
  };
  public static targets = [
    'searchResults',
    'keepButton',
    'clearButton',
    'cancelButton',
  ];

  declare private readonly typeValue: 'employee' | 'site';
  declare private currentDsNameValue: string;
  declare private currentValue: string;

  declare private searchResultsTarget: HTMLElement;
  declare private keepButtonTarget: HTMLButtonElement;
  declare private clearButtonTarget: HTMLButtonElement;
  declare private cancelButtonTarget: HTMLButtonElement;

  constructor(context: Context) {
    super(context);

    this.onResults = this.onResults.bind(this);
  }

  connect(): void {
    this.searchResultsTarget.addEventListener(
      EVENT_TASK_PICK_RESOURCE_RESULTS,
      this.onResults,
    );
  }

  disconnect(): void {
    this.searchResultsTarget.removeEventListener(
      EVENT_TASK_PICK_RESOURCE_RESULTS,
      this.onResults,
    );
  }

  public onStart(event: Event) {
    if (
      !('detail' in event) ||
      typeof event.detail !== 'object' ||
      !event.detail
    ) {
      throw new Error('Expected custom event with detail property');
    }

    const stimulusAction = event as Omit<CustomEvent, 'detail'> & {
      detail: {
        messageId?: string;
        dsName: string;
        dsType: 'employee' | 'site';
        inputName?: string;
        inputValue: string;
        actionButtonSelector: string;
      };
    };

    if (
      !('dsName' in stimulusAction.detail) ||
      !('dsType' in stimulusAction.detail) ||
      !('actionButtonSelector' in stimulusAction.detail)
    ) {
      throw new Error(
        'Expected dsName, dsType, and actionButtonSelector in detail, actual: ' +
          Object.keys(stimulusAction.detail).toString(),
      );
    }

    const { dsName, inputValue, actionButtonSelector, dsType } =
      stimulusAction.detail;

    if (dsType !== this.typeValue) {
      // ignore because type does not match
      return;
    }

    console.debug('[resource-picker] start picking', stimulusAction.detail);

    this.currentDsNameValue = dsName;
    this.currentValue = inputValue;

    toggleHidden(this.keepButtonTarget, !this.currentValue);
    toggleHidden(this.clearButtonTarget, !this.currentValue);
    toggleHidden(this.cancelButtonTarget, !!this.currentValue);

    this.dispatch('start', {
      detail: {
        dialog: {
          focusAfterClosed: actionButtonSelector,
          clearOnOpen: true,
        },
      },
    });
  }

  public onPick(event: Event) {
    if (
      !('params' in event) ||
      typeof event.params !== 'object' ||
      !event.params
    ) {
      throw new Error('Expected custom event with params property');
    }

    const stimulusAction = event as Omit<CustomEvent, 'detail'> & {
      params: {
        value: string;
      };
    };

    if (!('value' in stimulusAction.params)) {
      throw new Error(
        'Expected value in params, actual: ' +
          Object.keys(stimulusAction.params).toString(),
      );
    }

    // Unset old keep button
    this.unsetSelected();

    // Update "Keep" message
    this.currentValue = stimulusAction.params.value;
    this.onResults(event);

    this.dispatch('pick', {
      detail: {
        value: stimulusAction.params.value,
        dsName: this.currentDsNameValue,
      },
    });

    this.currentDsNameValue = '';
    this.currentValue = '';
  }

  public onClear() {
    this.dispatch('pick', {
      detail: { dsName: this.currentDsNameValue, value: '' },
    });

    this.currentDsNameValue = '';
    this.currentValue = '';
  }

  public onCancel() {
    this.dispatch('cancel', {
      detail: { dsName: this.currentDsNameValue },
    });

    this.currentDsNameValue = '';
    this.currentValue = '';
  }

  public onResults(_: Event) {
    console.debug('[resource-picker] on search results update');
    if (!this.currentValue) {
      return;
    }

    const id = this.currentValue.split('/').pop();
    if (!id) {
      return;
    }

    const row = this.element.querySelector('#pick-resource-' + id);
    if (!row) {
      return;
    }

    row.setAttribute('aria-selected', 'true');

    const button = row.querySelector(
      'button[type="button"][data-action~="tasks--resource-picker#onPick"]',
    );
    if (!button) {
      return;
    }

    button.firstElementChild!.textContent = 'Keep';
    button.classList.remove('--neutral');
    button.classList.add('--green');
  }

  private unsetSelected() {
    if (!this.currentValue) {
      return;
    }

    const id = this.currentValue.split('/').pop();
    if (!id) {
      return;
    }

    const row = this.element.querySelector('#pick-resource-' + id);
    if (!row) {
      return;
    }

    row.removeAttribute('aria-selected');

    const button = row.querySelector(
      'button[type="button"][data-action~="tasks--resource-picker#onPick"]',
    );
    if (!button) {
      return;
    }

    button.firstElementChild!.textContent = 'Pick';
    button.classList.add('--neutral');
    button.classList.remove('--green');
  }
}
